/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React, { useId } from 'react'
import clsx from 'clsx'
import UiFormLabel from './UiFormLabel'
import { uiFormControlDisabledClassName } from './UiFormControlStyles'

export interface UiInputProps
    extends React.InputHTMLAttributes<HTMLInputElement> {
    label?: React.ReactNode
    error?: string
    hasError?: boolean
    startIcon?: React.ReactNode
    endIcon?: React.ReactNode
    startIconInteractive?: boolean
    endIconInteractive?: boolean
    containerClassName?: string
}

const UiInput = React.forwardRef<HTMLInputElement, UiInputProps>(
    (
        {
            label,
            error,
            hasError,
            startIcon,
            endIcon,
            startIconInteractive,
            endIconInteractive,
            containerClassName,
            className,
            id,
            autoComplete = 'one-time-code',
            ...props
        },
        ref
    ) => {
        const generatedId = useId()
        const inputId = id || generatedId
        const errorId = `${inputId}-error`
        const isInvalid = !!error || !!hasError

        return (
            <div className={clsx('UiInput flex flex-col gap-1.5', containerClassName)}>
                {label && (
                    <UiFormLabel
                        htmlFor={inputId}
                        isRequired={props.required}
                    >
                        {label}
                    </UiFormLabel>
                )}
                <div className="relative">
                    {startIcon && (
                        <div
                            className={clsx(
                                'absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500 dark:text-gray-400',
                                startIconInteractive
                                    ? 'cursor-pointer'
                                    : 'pointer-events-none'
                            )}
                        >
                            {startIcon}
                        </div>
                    )}
                    <input
                        ref={ref}
                        id={inputId}
                        aria-invalid={isInvalid}
                        aria-describedby={error ? errorId : undefined}
                        autoComplete={autoComplete}
                        className={clsx(
                            'flex h-10 w-full rounded-lg border px-3 py-2 text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium transition-colors',
                            props.disabled
                                ? uiFormControlDisabledClassName
                                : 'border-gray-300 bg-white shadow-sm placeholder:text-gray-500 focus-visible:outline-none focus-visible:border-blue-500 focus-visible:ring-0 dark:border-gray-800 dark:bg-gray-950 dark:text-gray-100 dark:placeholder:text-gray-400 dark:focus-visible:border-blue-400',
                            startIcon && 'pl-10',
                            endIcon && 'pr-10',
                            isInvalid
                                ? 'border-red-500 focus-visible:border-blue-500 focus-visible:ring-0'
                                : !props.disabled && 'focus-visible:border-blue-500 focus-visible:ring-0',
                            className
                        )}
                        {...props}
                    />
                    {endIcon && (
                        <div
                            className={clsx(
                                'absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 dark:text-gray-400',
                                endIconInteractive
                                    ? 'cursor-pointer'
                                    : 'pointer-events-none'
                            )}
                        >
                            {endIcon}
                        </div>
                    )}
                </div>
                {error && (
                    <span
                        id={errorId}
                        className="text-xs text-red-500 mt-1"
                        aria-live="polite"
                    >
                        {error}
                    </span>
                )}
            </div>
        )
    }
)

UiInput.displayName = 'UiInput'

export default UiInput
